import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useToastStore } from '@/store/toastStore'

// Stripe test card numbers
const TEST_CARDS = [
  {
    type: 'Success',
    number: '4242 4242 4242 4242',
    cvc: 'Any 3 digits',
    expiry: 'Any future',
    icon: '✅',
  },
  {
    type: 'Declined',
    number: '4000 0000 0000 0002',
    cvc: 'Any',
    expiry: 'Any future',
    icon: '❌',
  },
  {
    type: 'Insufficient Funds',
    number: '4000 0000 0000 9995',
    cvc: 'Any',
    expiry: 'Any future',
    icon: '💸',
  },
  {
    type: 'Fraudulent',
    number: '4100 0000 0000 0019',
    cvc: 'Any',
    expiry: 'Any future',
    icon: '🚫',
  },
  {
    type: '3D Secure',
    number: '4000 0000 0000 3220',
    cvc: 'Any',
    expiry: 'Any future',
    icon: '🔐',
  },
]

export function StripeTestHelper() {
  const [isOpen, setIsOpen] = useState(false)
  const showToast = useToastStore((state) => state.showToast)

  // Only show in development/staging/cloudflare tunnel
  const isDev = import.meta.env.DEV ||
    window.location.hostname.includes('staging') ||
    window.location.hostname.includes('trycloudflare.com')
  if (!isDev) return null

  const copyToClipboard = async (cardNumber: string) => {
    // Remove spaces for easy pasting
    const cleanNumber = cardNumber.replace(/\s/g, '')
    try {
      await navigator.clipboard.writeText(cleanNumber)
      showToast('Card number copied!', 'success', 2000)
      setIsOpen(false)
    } catch {
      showToast('Failed to copy', 'error', 2000)
    }
  }

  return (
    <>
      {/* Floating test button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 z-50 w-12 h-12 rounded-full bg-purple-500 text-white shadow-lg flex items-center justify-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Test Cards"
      >
        <span className="text-xl">🧪</span>
      </motion.button>

      {/* Modal overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-4 safe-bottom"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span>🧪</span> Stripe Test Cards
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Test cards list */}
              <div className="space-y-2">
                {TEST_CARDS.map((card) => (
                  <button
                    key={card.type}
                    onClick={() => copyToClipboard(card.number)}
                    className="w-full p-3 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{card.icon}</span>
                        <span className="font-medium text-gray-900">{card.type}</span>
                      </div>
                      <span className="text-xs text-gray-400">Tap to copy</span>
                    </div>
                    <div className="mt-1 font-mono text-sm text-gray-600">{card.number}</div>
                    <div className="mt-1 text-xs text-gray-400">
                      CVC: {card.cvc} | Exp: {card.expiry}
                    </div>
                  </button>
                ))}
              </div>

              {/* Info note */}
              <p className="mt-4 text-xs text-gray-400 text-center">
                These are Stripe test cards for development only
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
